import { ErrorContainer, ErrorMessage, InlineLoadingText, LoadingState } from '../../../shared/styles/components/feedback.styles';

import { ActionButton } from '../../../shared/styles/components/integrations.styles';
import { BomVersionProps } from '../types/bom.types';
import { Divider } from '@mui/material';
import React from 'react';
import { formatBackendDateForDisplay } from '../../../shared/utils/fiscal-year.utils';
import { handleKeyDown } from '../../../shared/utils/keyboard.utils';
import { mixins } from '../../../shared/styles/core/utils';
import styled from '@emotion/styled';
import { theme } from '../../../shared/styles/core/theme';
import { tokens } from '../../../shared/styles/core/tokens';

export const SectionTitle = styled.span`
   padding: ${tokens.space.xs} ${tokens.space.md};
   font-size: ${tokens.font.size.sm};
   color: ${theme.colors.text.primary};
   text-transform: uppercase;
`;

export const VersionInfo = styled.span`
   margin-left: ${tokens.space.xs};
   font-size: ${tokens.font.size.sm};
   color: ${theme.colors.text.tertiary};
`;

export const DropdownMenu = styled.div`
   ${mixins.card};
   position: absolute;
   top: 100%;
   left: 0;
   z-index: ${tokens.zIndex.dropdown};
   min-width: 220px;
   margin-top: ${tokens.space.xs};
   padding: ${tokens.space.sm} 0;
`;

export const DropdownHeader = styled.div`
   padding: ${tokens.space.sm} ${tokens.space.md};
   font-size: ${tokens.font.size.sm};
   font-weight: ${tokens.font.weight.semibold};
   color: ${theme.colors.text.primary};
`;

export const DropdownItem = styled.div<{
    isActive?: boolean;
    isCurrent?: boolean;
    isAction?: boolean;
}>`
   ${mixins.hoverHighlight};
   padding: ${tokens.space.sm} ${tokens.space.md};
   color: ${(props) =>
        props.isAction || props.isActive || props.isCurrent
            ? theme.colors.border.focus
            : theme.colors.text.primary};
   font-weight: ${(props) =>
        props.isAction || props.isActive || props.isCurrent
            ? tokens.font.weight.semibold
            : tokens.font.weight.normal};
   display: flex;
   align-items: center;
`;

interface VersionsDropdownProps extends BomVersionProps {
    isOpen: boolean;
    isLoadingVersions: boolean;
    isLoadingVersionChange: boolean;
    onVersionSelect: (date: string) => void;
    onClearVersion: () => void;
    onOpenCloneModal: () => void;
    onClose: () => void;
    loadingVersion?: string | null;
    loadingState?: 'idle' | 'loading' | 'error' | 'success';
    handleKeyDown?: <T extends Element>(e: React.KeyboardEvent<T>, action: () => void) => void;
}

const VersionsDropdown: React.FC<VersionsDropdownProps> = ({
    isOpen,
    isLoadingVersions,
    isLoadingVersionChange,
    bomVersionHistory,
    currentBomValidFrom,
    error,
    onRetry,
    onVersionSelect,
    onClearVersion,
    onOpenCloneModal,
    onClose,
    loadingVersion = null,
    loadingState = 'idle',
    handleKeyDown: customKeyHandler
}) => {
    if (!isOpen) return null;

    const keyHandler = customKeyHandler || handleKeyDown;

    const isLoading = isLoadingVersions || isLoadingVersionChange;
    const effectiveLoadingState = loadingState !== 'idle'
        ? loadingState
        : isLoading
            ? 'loading'
            : error
                ? 'error'
                : 'success';

    const effectiveLoadingVersion = loadingVersion !== null
        ? loadingVersion
        : isLoadingVersions
            ? 'history'
            : isLoadingVersionChange
                ? 'loading'
                : null;

    return (
        <DropdownMenu
            onKeyDown={(e) => {
                if (e.key === 'Escape') {
                    e.preventDefault();
                    onClose();
                }
            }}
        >
            <DropdownHeader>BoM valid from</DropdownHeader>

            {effectiveLoadingState === 'loading' && effectiveLoadingVersion === 'history' && (
                <LoadingState>
                    <span>Loading versions...</span>
                </LoadingState>
            )}

            {effectiveLoadingState === 'error' && error && (
                <ErrorContainer>
                    <ErrorMessage>{error.message}</ErrorMessage>
                    {error.retryable && (
                        <ActionButton
                            onClick={onRetry}
                            tabIndex={0}
                            onKeyDown={(e) => keyHandler(e, onRetry)}
                        >
                            Retry
                        </ActionButton>
                    )}
                </ErrorContainer>
            )}

            {currentBomValidFrom && (
                <>
                    <VersionInfo>
                        Current: {formatBackendDateForDisplay(currentBomValidFrom)}
                    </VersionInfo>
                    <DropdownItem
                        onClick={onClearVersion}
                        isAction
                        tabIndex={0}
                        onKeyDown={(e) => keyHandler(e, onClearVersion)}
                    >
                        {effectiveLoadingState === 'loading' && effectiveLoadingVersion === 'default' ? (
                            <InlineLoadingText>
                                <span>Returning...</span>
                            </InlineLoadingText>
                        ) : (
                            'Return to default view'
                        )}
                    </DropdownItem>
                </>
            )}

            {bomVersionHistory.length > 0 && (
                <>
                    <Divider />
                    <SectionTitle>Versions</SectionTitle>
                    {bomVersionHistory.map((date: string) => (
                        <DropdownItem
                            key={date}
                            onClick={() => onVersionSelect(date)}
                            isActive={date === currentBomValidFrom}
                            tabIndex={0}
                            onKeyDown={(e) => keyHandler(e, () => onVersionSelect(date))}
                        >
                            {effectiveLoadingState === 'loading' && effectiveLoadingVersion === date ? (
                                <InlineLoadingText>
                                    <span>Loading...</span>
                                </InlineLoadingText>
                            ) : (
                                <>
                                    {formatBackendDateForDisplay(date)}
                                    <VersionInfo>
                                        {date === currentBomValidFrom && ' (current)'}
                                    </VersionInfo>
                                </>
                            )}
                        </DropdownItem>
                    ))}
                </>
            )}

            <Divider />
            <DropdownItem
                onClick={onOpenCloneModal}
                isAction
                tabIndex={0}
                onKeyDown={(e) => keyHandler(e, onOpenCloneModal)}
            >
                Clone new version
            </DropdownItem>
        </DropdownMenu>
    );
};

export default VersionsDropdown;
